import { useRef } from "react";
import { motion, useReducedMotion, useInView } from "framer-motion";
import { IMAGES } from "../data/content";

/**
 * HeroBrandEntrance — the "ta-dah" signature reveal.
 *
 * Sits as a full-width band DIRECTLY BELOW the hero (H1 + photo). The hero
 * settles first as the real intro; then the visitor scrolls a little and
 * My Rich Aunt and her necklace make a confident, slow, graceful entrance —
 * from the left, in sync with the chain being drawn.
 *
 * Design decisions locked with founder (Aug 22, 2026 · v4):
 *   - Triggered on scroll (`useInView` with `once: true`), never on load.
 *   - Wordmark reveals via a left-to-right clip-path sweep (~2.2s), NOT a
 *     rise-and-blur pop. Matches the chain's left-to-right draw so the two
 *     feel continuous — as if the necklace is pulling her name into view.
 *   - Chain draws slowly (1.8s) with the pen-tracing ease.
 *   - All 5 palette jewels preserved (tangerine, cobalt, grass, blossom, gold),
 *     each settling one after the other with a soft fade-up — no bouncing,
 *     no scale overshoot. Feels like real pearls landing on the chain.
 *   - Total sequence ~5.3s. Confident, ceremonial pacing.
 *   - Reduced-motion visitors see the settled state instantly.
 *
 * The persistent stacked navbar logo IDs the brand for above-the-fold
 * visitors; this component is the "meet your Aunt" moment.
 */

// Palette definitions with a highlight tint and rim shadow per color.
// The gradient is deliberately restrained — a small warm sheen at top-left,
// the main color across most of the surface, and a soft darkening at the
// bottom-right rim. Reads like polished enamel or a pearl, not a Christmas
// light. No palette-colored halo behind the pill (that's the tell-tale
// "tree bulb" effect); the depth comes only from the gradient + a single
// small specular kiss + a subtle drop-shadow.
const PALETTE = [
  { key: "tangerine", hex: "#FD6106", highlight: "#FFB278", rim: "#8F3603" },
  { key: "cobalt",    hex: "#0322F4", highlight: "#6B79F0", rim: "#02168A" },
  { key: "grass",     hex: "#0A9B06", highlight: "#5DC15A", rim: "#065904" },
  { key: "blossom",   hex: "#FD98D9", highlight: "#FFC5E7", rim: "#9C4577" },
  { key: "yellow",    hex: "#FDD006", highlight: "#FFE45C", rim: "#8C6E02" },
];

// Necklace geometry — three variants share the same 5 palette beads
// but differ in chain shape (and viewBox height + whether a pendant charm
// hangs off the center bead).
//
//   "wavy"          — original hero band: pearls float along an S-curve,
//                     each at its own height. viewBox 900×90.
//   "hanging"       — chain drapes like a real necklace with the center
//                     bead at the lowest point. viewBox 900×130.
//   "hanging-charm" — same drape as "hanging" plus the stacked wordmark
//                     logo dangling as a pendant charm from center bead.
//                     viewBox 900×230.
const SHAPES = {
  wavy: {
    viewBox: "0 0 900 90",
    chainPath: "M 20 44 C 120 20, 240 68, 340 44 S 560 12, 700 44 Q 800 60, 878 22",
    beads: [
      { ...PALETTE[0], cx: 100, cy: 34 },
      { ...PALETTE[1], cx: 280, cy: 60 },
      { ...PALETTE[2], cx: 460, cy: 30 },
      { ...PALETTE[3], cx: 630, cy: 34 },
      { ...PALETTE[4], cx: 800, cy: 42 },
    ],
    hasCharm: false,
  },
  hanging: {
    viewBox: "0 0 900 130",
    // Symmetric quadratic — lowest point at horizontal center. Both
    // ends attach at (30,20) and (870,20), the belly reaches y≈70 at
    // t=0.5. Bead centers sit on the curve at t=0.10/0.30/0.50/0.70/0.90.
    chainPath: "M 30 20 Q 450 120, 870 20",
    beads: [
      { ...PALETTE[0], cx: 114, cy: 38 },
      { ...PALETTE[1], cx: 282, cy: 62 },
      { ...PALETTE[2], cx: 450, cy: 70 },
      { ...PALETTE[3], cx: 618, cy: 62 },
      { ...PALETTE[4], cx: 786, cy: 38 },
    ],
    hasCharm: false,
  },
  "hanging-charm": {
    viewBox: "0 0 900 260",
    chainPath: "M 30 20 Q 450 120, 870 20",
    // Beads sit on the same catenary as "hanging" but with a wider gap
    // between the two center beads so the pendant charm can hang cleanly
    // between them (mirrors the reference photo: 2 beads left, gap for
    // pendant, 2 beads right... plus the outermost 5th bead on the right).
    // Slide-in order: index 0 → 1 → 2 → [pendant] → 3 → 4.
    beads: [
      { ...PALETTE[0], cx: 114, cy: 38 },
      { ...PALETTE[1], cx: 248, cy: 58 },
      { ...PALETTE[2], cx: 366, cy: 68 },
      { ...PALETTE[3], cx: 534, cy: 68 },
      { ...PALETTE[4], cx: 786, cy: 38 },
    ],
    hasCharm: true,
  },
};

// Timing (in seconds), relative to when the band scrolls into view.
// Sequence: hairline → wordmark clip reveal → chain draw → pills settle.
// For "hanging-charm" the pill sequence is extended to interleave the
// pendant: pill 1 → 2 → 3 → pendant descends → 4 → 5. Total ~9.2s.
// My Rich Aunt is not rushing — she is gliding to center stage.
const T_HAIRLINE_START = 0.00;
const T_HAIRLINE_DUR   = 1.40;
const T_WORDMARK_START = 0.60;
const T_WORDMARK_DUR   = 3.00;
const T_CHAIN_START    = 3.30;
const T_CHAIN_DUR      = 2.20;
const T_PILL_FIRST     = 5.30;
const T_PILL_STAGGER   = 0.28;
const T_PILL_DUR       = 0.90;
// Pendant timing (hanging-charm only). Starts ~t=6.65, just as bead 3
// (index 2) is completing its settle. Duration 1.55s → lands at ~t=8.20.
// Beads 4-5 (index 3-4) are then delayed to arrive AFTER the pendant is
// visibly settled, preserving the narrative order the founder specified.
const T_PENDANT_DELAY  = T_PILL_FIRST + 2 * T_PILL_STAGGER + T_PILL_DUR * 0.9;
const T_PENDANT_DUR    = 1.55;
const T_POST_PENDANT   = T_PENDANT_DELAY + T_PENDANT_DUR * 0.82;
// Idle life begins the moment the LAST pearl lands. Different for
// "hanging-charm" (which has 2 extra staggered beads after the pendant).
const T_IDLE_START     = T_PILL_FIRST + PALETTE.length * T_PILL_STAGGER + T_PILL_DUR * 0.35;

// Custom easings — slow, graceful, no bounce.
const EASE_GLIDE    = [0.32, 0.72, 0.24, 1];   // hairline sweep
const EASE_WORDMARK = [0.4,  0.0,  0.15, 1];   // Material "emphasized" — soft
                                               // start, glides through, gentle
                                               // deceleration at end (no snap).
const EASE_PEN      = [0.65, 0.05, 0.35, 1];   // pen-tracing feel for chain
const EASE_SETTLE   = [0.22, 0.9,  0.3,  1];   // ease-out for pearls landing

export const HeroBrandEntrance = ({
  autoPlay = false,
  className = "mt-20 lg:mt-28",
  necklaceShape = "wavy",
}) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.35 });
  // autoPlay bypass — used by the demo-reel end slate where the entrance
  // just appears (no scroll trigger). Still respects reduced-motion.
  const play = reduce || autoPlay ? true : inView;
  const shape = SHAPES[necklaceShape] || SHAPES.wavy;

  return (
    <div
      ref={ref}
      data-testid="hero-brand-entrance"
      aria-label="My Rich Aunt"
      className={`relative mx-auto flex w-full max-w-7xl flex-col items-center px-6 pb-6 pt-14 lg:px-8 lg:pt-20 ${className}`}
    >
      {/* Hairline separator — sweeps in from center outward */}
      <motion.span
        aria-hidden="true"
        data-testid="hero-brand-hairline"
        className="pointer-events-none absolute left-6 right-6 top-0 h-px lg:left-8 lg:right-8"
        style={{
          background:
            "linear-gradient(to right, transparent 0%, rgba(35,35,32,0.22) 18%, rgba(35,35,32,0.22) 82%, transparent 100%)",
          transformOrigin: "center",
        }}
        initial={reduce ? false : { scaleX: 0, opacity: 0 }}
        animate={play ? { scaleX: 1, opacity: 1 } : undefined}
        transition={{ delay: T_HAIRLINE_START, duration: T_HAIRLINE_DUR, ease: EASE_GLIDE }}
      />

      {/* Wordmark — marquee-scale, revealed left → right via clip-path.
          After the reveal completes, a gentle shimmer sweep runs across
          it every 8s on an infinite loop — subtle, warm, not flashy.  */}
      <span
        className="relative block"
        style={{ isolation: "isolate" }}
      >
        <motion.span
          data-testid="hero-brand-wordmark"
          initial={reduce ? false : { clipPath: "inset(0 100% 0 0)" }}
          animate={play ? { clipPath: "inset(0 0% 0 0)" } : undefined}
          transition={{ delay: T_WORDMARK_START, duration: T_WORDMARK_DUR, ease: EASE_WORDMARK }}
          className="block text-center uppercase text-ink"
          style={{
            fontFamily: "'Fraunces', 'Cormorant Garamond', serif",
            fontWeight: 900,
            fontVariationSettings: "'SOFT' 100, 'opsz' 144",
            fontSize: "clamp(2.8rem, 9vw, 8.5rem)",
            letterSpacing: "-0.02em",
            lineHeight: 0.9,
            // Fallback in case clip-path isn't supported — element still visible.
            WebkitClipPath: reduce ? "none" : undefined,
          }}
        >
          MY RICH AUNT
        </motion.span>
        {/* Shimmer sweep — a soft diagonal light band that glides across
            the wordmark on a slow, seamless loop. Sits on top with a
            screen blend so it just kisses the type, never obscures it. */}
        {!reduce && (
          <motion.span
            aria-hidden
            data-testid="hero-brand-wordmark-shimmer"
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(105deg, transparent 38%, rgba(255,255,255,0.55) 48%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,0.55) 52%, transparent 62%)",
              mixBlendMode: "overlay",
              maskImage: "linear-gradient(105deg, transparent 38%, black 48%, black 52%, transparent 62%)",
              WebkitMaskImage: "linear-gradient(105deg, transparent 38%, black 48%, black 52%, transparent 62%)",
              // Start off-canvas to the left; sweep to off-canvas right.
              transformOrigin: "center",
            }}
            initial={{ x: "-120%", opacity: 0 }}
            animate={play ? { x: ["-120%", "120%"], opacity: [0, 1, 1, 0] } : undefined}
            transition={{
              delay: T_IDLE_START,
              duration: 3.2,
              times: [0, 0.1, 0.9, 1],
              repeat: Infinity,
              repeatDelay: 5.6,
              ease: "easeInOut",
            }}
          />
        )}
      </span>

      {/* Necklace — hand-drawn chain + 5 palette jewels.
          After the reveal, the whole SVG breathes with a gentle sway on
          an infinite loop — a slow float up/down + a whisper of rotation.
          The chain feels warm and alive, never flashy. */}
      <motion.div
        style={{
          width: "min(74%, 740px)",
          marginTop: "clamp(14px, 1.4vw, 26px)",
          transformOrigin: "center top",
        }}
        animate={
          play && !reduce
            ? { y: [0, -3, 0, 2, 0], rotate: [0, 0.35, 0, -0.35, 0] }
            : undefined
        }
        transition={{
          delay: T_IDLE_START,
          duration: 8.4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <svg
          data-testid="hero-brand-necklace"
          viewBox={shape.viewBox}
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          style={{
            display: "block",
            width: "100%",
            height: "auto",
            overflow: "visible",
          }}
        >
          <defs>
            {/* One radial gradient per palette bead. Deliberately gentle:
                a small warm sheen at 25%/20% blends quickly into the main
                color, and the rim only just darkens toward the bottom-right.
                Restrained to keep the "polished enamel" look rather than
                a tree-light glow. */}
            {PALETTE.map((p) => (
              <radialGradient
                key={p.key}
                id={`bead-grad-${p.key}`}
                cx="30%"
                cy="25%"
                r="85%"
                fx="28%"
                fy="22%"
              >
                <stop offset="0%"  stopColor={p.highlight} stopOpacity="0.85" />
                <stop offset="22%" stopColor={p.hex} stopOpacity="1" />
                <stop offset="78%" stopColor={p.hex} stopOpacity="1" />
                <stop offset="100%" stopColor={p.rim} stopOpacity="1" />
              </radialGradient>
            ))}
            {/* Gunmetal gradient for the pendant bail (only used by
                "hanging-charm") — reads as brushed silver-black, not gold.
                Wider tonal range so it stays visible against the warm sand
                background even at small sizes. */}
            <linearGradient id="pendant-bail-gunmetal" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%"  stopColor="#B8BBC0" />
              <stop offset="50%" stopColor="#4A4D52" />
              <stop offset="100%" stopColor="#1A1C1F" />
            </linearGradient>
            {/* Drop-shadow filter for the wordmark charm — gives the
                otherwise-flat PNG a subtle physical lift so it reads
                as a 3D casted metal charm, not a sticker. */}
            <filter id="pendant-drop-shadow" x="-25%" y="-15%" width="150%" height="140%">
              <feDropShadow dx="0" dy="3" stdDeviation="3.2" floodColor="#0A0A0B" floodOpacity="0.55" />
            </filter>
          </defs>

          <motion.path
            d={shape.chainPath}
            fill="none"
            stroke="#232320"
            strokeOpacity={0.4}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={reduce ? false : { pathLength: 0 }}
            animate={play ? { pathLength: 1 } : undefined}
            transition={{ delay: T_CHAIN_START, duration: T_CHAIN_DUR, ease: EASE_PEN }}
          />
          {shape.beads.map((p, i) => {
            // For the "hanging-charm" variant the sequence is strictly:
            //   bead 1 → 2 → 3 → pendant slides down + settles → 4 → 5.
            // So beads at index >= 3 (the 4th and 5th) wait until AFTER
            // the pendant has visibly landed before they arrive.
            const beadDelay =
              shape.hasCharm && i >= 3
                ? T_POST_PENDANT + (i - 3) * T_PILL_STAGGER
                : T_PILL_FIRST + i * T_PILL_STAGGER;
            return (
              <motion.g
                key={p.key}
                data-testid={`hero-brand-jewel-${p.key}`}
                style={{
                  filter: "drop-shadow(0 3px 5px rgba(35,35,32,0.28))",
                  transformOrigin: `${p.cx}px ${p.cy}px`,
                }}
                initial={reduce ? false : { opacity: 0, y: 6 }}
                animate={play ? { opacity: 1, y: 0 } : undefined}
                transition={{
                  delay: beadDelay,
                  duration: T_PILL_DUR,
                  ease: EASE_SETTLE,
                }}
              >
                {/* The bead itself — 3D lit via the restrained gradient. */}
                <ellipse
                  cx={p.cx}
                  cy={p.cy}
                  rx={34}
                  ry={12}
                  fill={`url(#bead-grad-${p.key})`}
                />
                {/* One small, low-opacity specular kiss at top-left. Soft
                    enough to read as polished enamel, not a lit bulb. */}
                <ellipse
                  cx={p.cx - 12}
                  cy={p.cy - 4.5}
                  rx={7}
                  ry={2}
                  fill="#FFFFFF"
                  opacity={0.35}
                />
              </motion.g>
            );
          })}

          {/* Pendant charm — the stacked "MY RICH AUNT" wordmark rendered
              as a 3D black/gunmetal-grey pendant, hanging from a silver
              bail at the center of the chain. Sequence: appears AFTER
              bead 3 lands, slides down while gently swinging like a real
              pendant, then settles into a slow perpetual dangle. */}
          {shape.hasCharm && (
            <motion.g
              data-testid="hero-brand-pendant"
              style={{ transformOrigin: "450px 70px" }}
              initial={reduce ? false : { opacity: 0, y: -34, rotate: -6 }}
              animate={
                play
                  ? { opacity: 1, y: 0, rotate: 0 }
                  : undefined
              }
              transition={{
                delay: T_PENDANT_DELAY,
                opacity: { duration: 0.55 },
                y:       { duration: T_PENDANT_DUR, ease: [0.22, 1, 0.36, 1] },
                rotate:  { duration: 2.4,  ease: [0.22, 1, 0.28, 1] },
              }}
            >
              {/* Perpetual idle sway — starts once the initial drop
                  finishes. Very small angle, slow period so it reads as
                  physics (a heavy charm at rest), never a metronome. */}
              <motion.g
                style={{ transformOrigin: "450px 70px" }}
                animate={
                  play && !reduce
                    ? { rotate: [0, 1.2, 0, -1.2, 0] }
                    : undefined
                }
                transition={{
                  delay: T_PENDANT_DELAY + T_PENDANT_DUR + 1.0,
                  duration: 5.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                {/* Bail connector — silver strap from the chain's belly
                    down to the bail ring. Kept thick enough to read at
                    the small sizes the hero renders at. */}
                <line
                  x1="450"
                  y1="70"
                  x2="450"
                  y2="96"
                  stroke="url(#pendant-bail-gunmetal)"
                  strokeWidth={2.6}
                  strokeLinecap="round"
                />
                {/* Bail ring — brushed silver oval the charm hangs from */}
                <ellipse
                  cx="450"
                  cy="106"
                  rx="9"
                  ry="7.5"
                  fill="none"
                  stroke="url(#pendant-bail-gunmetal)"
                  strokeWidth={3}
                />
                {/* Subtle ground shadow beneath the charm — grounds the
                    pendant so it doesn't feel like it's floating. */}
                <ellipse
                  cx="450"
                  cy="252"
                  rx="72"
                  ry="4.5"
                  fill="rgba(35,35,32,0.24)"
                />
                {/* Wordmark charm — the stacked "MY RICH AUNT" logo
                    rendered as crisp SVG (not the wordmark PNG, which
                    pixelates at the small hero size). Cap-and-tassel
                    glyph sits above the "M" to match the site header
                    lockup. Rendered in near-black so it reads as a
                    gunmetal casted charm against the warm sand background. */}
                <g
                  transform="translate(450, 120)"
                  filter="url(#pendant-drop-shadow)"
                  style={{
                    fontFamily: "'Fraunces', 'Cormorant Garamond', 'Playfair Display', serif",
                    fontWeight: 900,
                    fill: "#0F0F10",
                  }}
                >
                  {/* Graduation cap — mortarboard silhouette (rhombus) with
                      a small under-crown, tassel dangling from the front-
                      right corner into a small bead. Sized to sit tucked
                      above the "MY" line at ~48 SVG units wide. */}
                  {/* Mortarboard top (diamond) */}
                  <path
                    d="M -24 -4 L 0 -16 L 24 -4 L 0 8 Z"
                    fill="#0F0F10"
                  />
                  {/* Under-crown (small trapezoid tucked below the diamond) */}
                  <path
                    d="M -9 5 L 9 5 L 6 12 L -6 12 Z"
                    fill="#0F0F10"
                  />
                  {/* Tassel — straight cord dangling from front-right corner
                      of the mortarboard, ending in a small bead */}
                  <line
                    x1="16"
                    y1="-1"
                    x2="16"
                    y2="12"
                    stroke="#0F0F10"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                  <circle cx="16" cy="14.5" r="2.4" fill="#0F0F10" />
                  {/* Stacked wordmark — Fraunces 900, three lines
                      "MY / RICH / AUNT". Sized so the widest word ("RICH")
                      sits ~110 SVG units wide. */}
                  <text
                    x="0"
                    y="42"
                    textAnchor="middle"
                    fontSize="30"
                    letterSpacing="-0.02em"
                    style={{ fontVariationSettings: "'SOFT' 100, 'opsz' 144" }}
                  >
                    MY
                  </text>
                  <text
                    x="0"
                    y="76"
                    textAnchor="middle"
                    fontSize="30"
                    letterSpacing="-0.02em"
                    style={{ fontVariationSettings: "'SOFT' 100, 'opsz' 144" }}
                  >
                    RICH
                  </text>
                  <text
                    x="0"
                    y="110"
                    textAnchor="middle"
                    fontSize="30"
                    letterSpacing="-0.02em"
                    style={{ fontVariationSettings: "'SOFT' 100, 'opsz' 144" }}
                  >
                    AUNT
                  </text>
                </g>
              </motion.g>
            </motion.g>
          )}
        </svg>
      </motion.div>
    </div>
  );
};
